// API Health Monitor - Detects when the connect_api backend is unavailable
// and redirects users to an error page after consecutive failures

interface FailureRecord {
  timestamp: number;
  status: number | 'network';
  url: string;
  method: string;
}

interface ApiHealthState {
  failures: FailureRecord[];
  redirected: boolean;
}

// Configuration
const CONFIG = {
  // Number of failures required to trigger redirect
  FAILURE_THRESHOLD: 3,
  // Time window - failures older than this don't count (10 seconds)
  TIME_WINDOW_MS: 10_000,
  // Debounce rapid failures to the SAME endpoint only (1 second)
  // Different endpoints failing rapidly still count as separate failures
  SAME_ENDPOINT_DEBOUNCE_MS: 1_000,
  // Status codes considered server errors (API is down)
  SERVER_ERROR_CODES: [500, 501, 502, 503, 504, 520, 521, 522, 523, 524],
};

// Endpoints that might fail normally and should not count toward health monitoring
const EXCLUDED_ENDPOINTS = [
  '/api/auth/csrf_token',
];

// Public routes where monitoring should be disabled
const PUBLIC_ROUTES = [
  '/customers/login',
];

class ApiHealthMonitor {
  private state: ApiHealthState = {
    failures: [],
    redirected: false,
  };

  /**
   * Check if a status code indicates a server error (API is down)
   */
  isServerError(status: number): boolean {
    return CONFIG.SERVER_ERROR_CODES.includes(status);
  }

  /**
   * Check if an endpoint should be excluded from monitoring
   */
  isExcludedEndpoint(url: string): boolean {
    return EXCLUDED_ENDPOINTS.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if current page should be monitored
   * Public routes (login, etc.) are excluded
   */
  shouldMonitor(): boolean {
    const pathname = window.location.pathname;
    return !PUBLIC_ROUTES.some(route => pathname.startsWith(route));
  }

  /**
   * Remove failures older than the time window
   */
  private pruneStaleFailures(): void {
    const cutoff = Date.now() - CONFIG.TIME_WINDOW_MS;
    this.state.failures = this.state.failures.filter(f => f.timestamp > cutoff);
  }

  /**
   * Check if this is a rapid duplicate failure to the same endpoint
   * Only debounces if the SAME URL failed within SAME_ENDPOINT_DEBOUNCE_MS
   * Different URLs failing rapidly should count as separate failures
   */
  private isDuplicateFailure(url: string): boolean {
    const now = Date.now();
    const recentSameUrlFailure = this.state.failures.find(
      f => f.url === url && (now - f.timestamp) < CONFIG.SAME_ENDPOINT_DEBOUNCE_MS
    );
    return !!recentSameUrlFailure;
  }

  /**
   * Record a server error or network failure
   */
  recordFailure(status: number | 'network', url: string, method: string): void {
    // Skip if already redirected
    if (this.state.redirected) {
      return;
    }

    // Skip excluded endpoints
    if (this.isExcludedEndpoint(url)) {
      console.debug('[ApiHealthMonitor] Skipping excluded endpoint:', url);
      return;
    }

    // Skip if same endpoint failed very recently (retry loop protection)
    if (this.isDuplicateFailure(url)) {
      console.debug('[ApiHealthMonitor] Skipping duplicate failure for same URL:', url);
      return;
    }

    // Prune stale failures first
    this.pruneStaleFailures();

    // Record this failure
    const failure: FailureRecord = {
      timestamp: Date.now(),
      status,
      url,
      method,
    };
    this.state.failures.push(failure);

    console.warn('[ApiHealthMonitor] Recorded failure:', {
      failure,
      totalFailures: this.state.failures.length,
      threshold: CONFIG.FAILURE_THRESHOLD,
    });
  }

  /**
   * Record a successful request - resets the failure counter
   * Any 2xx or 4xx response indicates the API is reachable
   */
  recordSuccess(): void {
    if (this.state.redirected) {
      return;
    }

    // Reset failures on any successful request
    if (this.state.failures.length > 0) {
      console.debug('[ApiHealthMonitor] Clearing failures on success, count was:', this.state.failures.length);
      this.state.failures = [];
    }
  }

  /**
   * Check if we should redirect to the error page
   */
  shouldRedirect(): boolean {
    // Prune stale failures before checking
    this.pruneStaleFailures();
    return this.state.failures.length >= CONFIG.FAILURE_THRESHOLD;
  }

  /**
   * Redirect to the error page
   */
  redirectToErrorPage(): void {
    // Prevent multiple redirects
    if (this.state.redirected) {
      return;
    }
    this.state.redirected = true;

    const errorPageUrl = import.meta.env.ERROR_PAGE_URL;

    if (!errorPageUrl) {
      console.error('[ApiHealthMonitor] ERROR_PAGE_URL not configured - cannot redirect');
      return;
    }

    // Build URL with debug params for troubleshooting
    const url = new URL(errorPageUrl);
    url.searchParams.set('source', 'api_health_monitor');
    url.searchParams.set('app', 'admin');
    url.searchParams.set('failures', String(this.state.failures.length));

    // Log detailed info for debugging
    console.error('[ApiHealthMonitor] Redirecting to error page', {
      reason: 'consecutive_api_failures',
      failures: this.state.failures,
      threshold: CONFIG.FAILURE_THRESHOLD,
      timeWindowMs: CONFIG.TIME_WINDOW_MS,
      redirectUrl: url.toString(),
    });

    // Perform redirect (preserves session - no auth state clearing)
    window.location.href = url.toString();
  }

  /**
   * Get debug info for troubleshooting
   */
  getDebugInfo(): object {
    this.pruneStaleFailures();
    return {
      failures: this.state.failures,
      failureCount: this.state.failures.length,
      threshold: CONFIG.FAILURE_THRESHOLD,
      timeWindowMs: CONFIG.TIME_WINDOW_MS,
      sameEndpointDebounceMs: CONFIG.SAME_ENDPOINT_DEBOUNCE_MS,
      redirected: this.state.redirected,
      shouldRedirect: this.shouldRedirect(),
      currentPath: window.location.pathname,
      isMonitoring: this.shouldMonitor(),
      errorPageUrl: import.meta.env.ERROR_PAGE_URL || '(not configured)',
    };
  }

  /**
   * Reset state (for testing)
   */
  reset(): void {
    this.state = {
      failures: [],
      redirected: false,
    };
  }
}

// Singleton instance
export const apiHealthMonitor = new ApiHealthMonitor();

// Expose debug method in development for troubleshooting via browser console
if (import.meta.env.DEV) {
  (window as unknown as Record<string, () => object>).__apiHealthDebug = () => apiHealthMonitor.getDebugInfo();
}
